import { useMemo, useState } from "react";

import type { TableSortDirection } from "@/components/common/SortableTableCell";

type SortableValue = boolean | number | string | null | undefined;

/** Provides locale-aware client-side sorting for standalone admin list tables. */
export const useTableSorting = <TItem, TColumn extends string>(
  items: readonly TItem[],
  getValue: (item: TItem, column: TColumn) => SortableValue,
  initialColumn: TColumn,
  locale?: string,
  initialDirection: TableSortDirection = "asc",
) => {
  const [sortColumn, setSortColumn] = useState<TColumn>(initialColumn);
  const [sortDirection, setSortDirection] = useState<TableSortDirection>(initialDirection);

  const handleSort = (column: TColumn) => {
    if (column === sortColumn) {
      setSortDirection((current) => (current === "asc" ? "desc" : "asc"));
      return;
    }

    setSortColumn(column);
    setSortDirection("asc");
  };

  const sortedItems = useMemo(() => {
    const collator = new Intl.Collator(locale, { numeric: true, sensitivity: "base" });

    return [...items].sort((a, b) => {
      const aValue = getValue(a, sortColumn);
      const bValue = getValue(b, sortColumn);

      if (aValue == null) return bValue == null ? 0 : 1;
      if (bValue == null) return -1;

      const result =
        typeof aValue === "number" && typeof bValue === "number"
          ? aValue - bValue
          : collator.compare(String(aValue), String(bValue));

      return sortDirection === "asc" ? result : -result;
    });
  }, [getValue, items, locale, sortColumn, sortDirection]);

  return { sortColumn, sortDirection, handleSort, sortedItems };
};
